import { useCallback, useRef, useEffect } from 'react';

/**
 * Drawer snap point states
 */
export enum DrawerSnapPoint {
  Minimized = 'minimized',
  Partial = 'partial', // split-view: tall enough for one listing card
  Full = 'full' // original list view, no drawer UI
}

const DEFAULT_DRAG_THRESHOLD = 0.15; // ratio of screen height — low threshold for responsive snapping
const DEFAULT_VELOCITY_THRESHOLD = 0.5; // pixels/ms
const DEFAULT_DRAG_HANDLE_HEIGHT = 60; // in pixels
const MIN_DRAG_DISTANCE_TO_START = 8; // in pixels. Error margin to prevent accidental drags
const DEFAULT_PARTIAL_DRAG_RATIO = 0.45;

/**
 * Options for the drawer gestures hook
 */
export interface UseDrawerGesturesOptions {
  /** Current drawer state */
  currentState: DrawerSnapPoint;
  
  /** Callback when state should change */
  onStateChange: (state: DrawerSnapPoint) => void;
  
  /** Drag threshold as percentage (0-1). */
  dragThreshold?: number;
  
  /** Velocity threshold in px/ms. */
  velocityThreshold?: number;
  
  /** Whether gestures are enabled. Default: true */
  enabled?: boolean;
  
  /** Height of draggable area at top (in px)*/
  dragHandleHeight?: number;
  
  /** Drag ratio (0-1) threshold for stopping at partial when swiping up from minimized. 0 = always skip to full. Default: 0.45 */
  partialDragRatio?: number;
}

/**
 * Return type for the drawer gestures hook
 */
export interface UseDrawerGesturesReturn {
  /** Touch start handler */
  handleTouchStart: (e: React.TouchEvent) => void;
  
  /** Touch move handler */
  handleTouchMove: (e: React.TouchEvent) => void;
  
  /** Touch end handler */
  handleTouchEnd: (e: React.TouchEvent) => void;
  
  /** Whether currently dragging (ref-based, no re-render) */
  isDragging: boolean;
  
  /** Current drag offset in pixels (ref-based, no re-render) */
  currentOffset: number;
  
  /** Ref to attach to container element */
  containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Get the next snap point in sequence
 */
function getNextState(current: DrawerSnapPoint): DrawerSnapPoint {
  switch (current) {
    case DrawerSnapPoint.Minimized:
      return DrawerSnapPoint.Partial;
    case DrawerSnapPoint.Partial:
      return DrawerSnapPoint.Full;
    case DrawerSnapPoint.Full:
      return DrawerSnapPoint.Full; // Already at max
    default:
      return current;
  }
}

/**
 * Get the previous snap point in sequence
 */
function getPrevState(current: DrawerSnapPoint): DrawerSnapPoint {
  switch (current) {
    case DrawerSnapPoint.Full:
      return DrawerSnapPoint.Partial;
    case DrawerSnapPoint.Partial:
      return DrawerSnapPoint.Minimized;
    case DrawerSnapPoint.Minimized:
      return DrawerSnapPoint.Minimized; // Already at min
    default:
      return current;
  }
}

/**
 * Determine target snap point. When swiping up from minimized, skips partial
 * and goes straight to full if dragRatio exceeds partialDragRatio.
 */
function getTargetState(
  current: DrawerSnapPoint,
  direction: 'up' | 'down',
  dragRatio: number,
  partialDragRatio: number
): DrawerSnapPoint {
  if (direction === 'up') {
    if (current === DrawerSnapPoint.Minimized && dragRatio >= partialDragRatio) {
      return DrawerSnapPoint.Full;
    }
    return getNextState(current);
  }
  return getPrevState(current);
}

/**
 * Hook for handling drawer touch gestures and snap points.
 * 
 * Performance: Uses refs and direct DOM manipulation during drag
 * to avoid React re-renders on every touch move. The transform is
 * applied via requestAnimationFrame for compositor-friendly 60fps updates.
 */
export function useDrawerGestures({
  currentState,
  onStateChange,
  dragThreshold = DEFAULT_DRAG_THRESHOLD,
  velocityThreshold = DEFAULT_VELOCITY_THRESHOLD,
  enabled = true,
  dragHandleHeight = DEFAULT_DRAG_HANDLE_HEIGHT,
  partialDragRatio = DEFAULT_PARTIAL_DRAG_RATIO,
}: UseDrawerGesturesOptions): UseDrawerGesturesReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const isDraggingRef = useRef(false);
  const currentOffsetRef = useRef(0);
  const touchStartY = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);
  const initialState = useRef<DrawerSnapPoint>(currentState);
  const rafId = useRef<number | null>(null);

  const currentStateRef = useRef(currentState);
  useEffect(() => {
    currentStateRef.current = currentState;
  }, [currentState]);

  const applyDragTransform = useCallback((offsetY: number) => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
    }
    rafId.current = requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;
      const baseTransform = el.dataset.baseTransform || 'translateY(0)';
      el.style.transition = 'none';
      el.style.transform = `${baseTransform} translateY(${offsetY}px)`;
      rafId.current = null;
    });
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      
      const container = containerRef.current;
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const touchY = e.touches[0].clientY;
      const relativeY = touchY - containerRect.top;

      if (relativeY <= dragHandleHeight) {
        touchStartY.current = e.touches[0].clientY;
        touchStartTime.current = Date.now();
        initialState.current = currentStateRef.current;
        isDraggingRef.current = false;
        currentOffsetRef.current = 0;
        container.dataset.baseTransform = container.style.transform || '';
      }
    },
    [enabled, dragHandleHeight]
  );
  
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || touchStartY.current === null) return;
      
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - touchStartY.current;
      const absDeltaY = Math.abs(deltaY);
      
      if (!isDraggingRef.current && absDeltaY > MIN_DRAG_DISTANCE_TO_START) {
        isDraggingRef.current = true;
        containerRef.current?.classList.add('dragging');
      }
      
      if (isDraggingRef.current) {
        currentOffsetRef.current = deltaY;
        applyDragTransform(deltaY);
      }
    },
    [enabled, applyDragTransform]
  );
  
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || touchStartY.current === null) return;
      
      containerRef.current?.classList.remove('dragging');
      
      if (isDraggingRef.current && touchStartTime.current !== null) {
        const endY = e.changedTouches[0].clientY;
        const startY = touchStartY.current;
        const deltaY = endY - startY;
        const absDeltaY = Math.abs(deltaY);
        
        const duration = Date.now() - touchStartTime.current;
        const velocity = duration > 0 ? absDeltaY / duration : 0;
        
        let targetState = initialState.current;
        const screenHeight = window.innerHeight;
        const dragRatio = absDeltaY / screenHeight;
        const direction: 'up' | 'down' = deltaY < 0 ? 'up' : 'down';
        
        if (velocity > velocityThreshold) {
          targetState = getTargetState(initialState.current, direction, dragRatio, partialDragRatio);
        } else if (dragRatio > dragThreshold) {
          targetState = getTargetState(initialState.current, direction, dragRatio, partialDragRatio);
        }
        
        const el = containerRef.current;
        if (el) {
          delete el.dataset.baseTransform;
        }
        
        onStateChange(targetState);
      } else {
        const el = containerRef.current;
        if (el) {
          delete el.dataset.baseTransform;
        }
      }
      
      touchStartY.current = null;
      touchStartTime.current = null;
      isDraggingRef.current = false;
      currentOffsetRef.current = 0;
      
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    },
    [enabled, onStateChange, dragThreshold, velocityThreshold, partialDragRatio]
  );

  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);
  
  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isDragging: isDraggingRef.current,
    currentOffset: currentOffsetRef.current,
    containerRef,
  };
}
